/**
 * Base HTTP client factory
 * Platform-agnostic HTTP wrapper that works on both web and React Native
 */

export class ApiError extends Error {
  action?: string;
  status: number;
  /** Full parsed response body (preserves quota info, etc.) */
  responseData?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    action?: string,
    responseData?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.action = action;
    this.responseData = responseData;
  }

  /**
   * Returns a user-safe error message from any caught error.
   * - 4xx ApiErrors: returns server message (server already sanitizes these)
   * - 5xx ApiErrors / network errors / unknown: returns the provided fallback
   */
  static getUserMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.status < 500) {
      return err.message;
    }
    return fallback;
  }
}

export type AuthFetchFunction = (url: string, init?: RequestInit) => Promise<Response>;

export interface HttpClientOptions {
  authFetch: AuthFetchFunction;
  baseUrl: string;
}

/**
 * Build a full URL from path + optional base.
 * Handles empty baseUrl (web dev with Vite proxy) by using path directly.
 */
function buildUrl(
  path: string,
  baseUrl: string,
  queryParams?: Record<string, string | number | boolean>,
): string {
  const fullPath = baseUrl ? `${baseUrl}${path}` : path;
  if (!queryParams || Object.keys(queryParams).length === 0) return fullPath;
  const qs = Object.entries(queryParams)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `${fullPath}?${qs}`;
}

export function createHttpClient(options: HttpClientOptions) {
  const { authFetch, baseUrl } = options;

  return {
    /**
     * GET request
     */
    async get<T>(
      path: string,
      queryParams?: Record<string, string | number | boolean>,
    ): Promise<T> {
      const url = buildUrl(path, baseUrl, queryParams);

      const res = await authFetch(url);

      if (!res.ok) {
        const errorBody = (await res.json().catch(() => ({ error: 'Request failed' }))) as {
          error?: string;
          action?: string;
        };
        throw new ApiError(errorBody.error || 'Request failed', res.status, errorBody.action);
      }

      return res.json() as Promise<T>;
    },

    /**
     * POST request
     */
    async post<T>(path: string, body?: unknown): Promise<T> {
      const url = buildUrl(path, baseUrl);

      const res = await authFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        const errorBody = (await res.json().catch(() => ({ error: 'Request failed' }))) as Record<
          string,
          unknown
        >;
        throw new ApiError(
          (errorBody.error as string) || 'Request failed',
          res.status,
          errorBody.action as string | undefined,
          errorBody,
        );
      }

      return res.json() as Promise<T>;
    },

    /**
     * PUT request
     */
    async put<T>(path: string, body?: unknown): Promise<T> {
      const url = buildUrl(path, baseUrl);

      const res = await authFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        const errorBody = (await res.json().catch(() => ({ error: 'Request failed' }))) as {
          error?: string;
          action?: string;
        };
        throw new ApiError(errorBody.error || 'Request failed', res.status, errorBody.action);
      }

      return res.json() as Promise<T>;
    },

    /**
     * PATCH request
     */
    async patch<T>(path: string, body?: unknown): Promise<T> {
      const url = buildUrl(path, baseUrl);

      const res = await authFetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        const errorBody = (await res.json().catch(() => ({ error: 'Request failed' }))) as {
          error?: string;
          action?: string;
        };
        throw new ApiError(errorBody.error || 'Request failed', res.status, errorBody.action);
      }

      return res.json() as Promise<T>;
    },

    /**
     * DELETE request
     */
    async delete<T>(path: string): Promise<T> {
      const url = buildUrl(path, baseUrl);

      const res = await authFetch(url, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorBody = (await res.json().catch(() => ({ error: 'Request failed' }))) as {
          error?: string;
          action?: string;
        };
        throw new ApiError(errorBody.error || 'Request failed', res.status, errorBody.action);
      }

      return res.json() as Promise<T>;
    },

    /**
     * DELETE request with no response body (returns void)
     */
    async deleteNoContent(path: string): Promise<void> {
      const url = buildUrl(path, baseUrl);

      const res = await authFetch(url, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorBody = (await res.json().catch(() => ({ error: 'Request failed' }))) as {
          error?: string;
          action?: string;
        };
        throw new ApiError(errorBody.error || 'Request failed', res.status, errorBody.action);
      }
    },

    /**
     * Raw authFetch for custom requests (e.g., SSE streaming)
     */
    rawFetch: authFetch,

    /**
     * Base URL for constructing custom URLs
     */
    baseUrl,
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
